import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { postSharedBasketDownload } from '@/lib/api';
import { ApiError, SharedBasketItemInput } from '@/lib/types';
import { DownloadSignedUrlFile } from '@/lib/download';
import { getSharedBasketErrorCode } from '@/lib/sharedBasketError';
import { sharedBasketQueryKeys } from '@/lib/sharedBasketQueryKeys';
import { useNotifications } from './useNotifications';

// Every gate re-runs server-side on download, so a link can die between the
// page rendering and the button being pressed. That 410 is a normal outcome
// here, not a crash: it gets its own message and re-runs the basket query so
// the page falls into the dead-end state behind the toast.
export const useSharedBasketDownload = (token: string) => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotifications();

  const { mutate, isPending } = useMutation<
    DownloadSignedUrlFile[],
    AxiosError<ApiError>,
    SharedBasketItemInput[] | undefined
  >({
    mutationFn: (items) => postSharedBasketDownload(token, items),
    onSuccess: (failedFiles) => {
      if (failedFiles.length === 0) {
        showSuccess('Download started');
        return;
      }
      showError(
        `${failedFiles.length} of the files could not be downloaded. Please try again.`,
      );
    },
    onError: (error) => {
      const code = getSharedBasketErrorCode(error);

      if (code === 'LINK_EXPIRED' || code === 'LINK_DISABLED') {
        showError(
          'This link is no longer active, so nothing was downloaded.',
        );
        // Re-runs the basket query, which now 410s - the page renders the
        // matching dead-end instead of a list that can no longer be used.
        queryClient.invalidateQueries({
          queryKey: sharedBasketQueryKeys.byToken(token),
        });
        return;
      }

      if (code === 'NOT_INTENDED_RECIPIENT') {
        showError('This link was shared with a different account.');
        queryClient.invalidateQueries({
          queryKey: sharedBasketQueryKeys.byToken(token),
        });
        return;
      }

      showError(
        error.response?.data?.error?.message ||
          'Could not download these files. Please try again.',
      );
    },
  });

  const download = useCallback(
    (items?: SharedBasketItemInput[]) => {
      if (isPending) return;
      mutate(items);
    },
    [isPending, mutate],
  );

  return { download, isDownloading: isPending };
};

export default useSharedBasketDownload;
